import classNames from 'classnames';
import { concat } from 'lodash';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormGroup, Label } from 'reactstrap';
import { FieldValues, UseFormReturn } from "react-hook-form";
import { usePasswordStrength } from '../hooks';
import FancyProgress from './FancyProgress';

type PasswordFormGroupProps = {
    id: string,
    form: UseFormReturn<FieldValues>
}

const PasswordFormGroup = ({ id, form }: PasswordFormGroupProps) => {

    const { t } = useTranslation('common');

    const { register: registerInput, watch, formState, setValue } = form;
    const { passwordStrength: calculatePasswordStrength } = usePasswordStrength();

    const { isSubmitted, errors } = formState;
    const [password, setPassword] = useState<string | null>(null);
    const [passwordStrength, setPasswordStrength] = useState(() => calculatePasswordStrength(password??''));

    const passwordIsStrong = useCallback(() => passwordStrength.value?.score > 5, [passwordStrength]);

    const { onChange: onPasswordChange } = useMemo(() => registerInput('password', {
        required: t('Campo obbligatorio') as string,
        validate: { passwordIsStrong }
    }), [registerInput, passwordIsStrong, t]);

    const handlePasswordChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
        const password = e.target.value;
        setPassword(password);
        setPasswordStrength(calculatePasswordStrength(password));
        onPasswordChange(e);
    }, [calculatePasswordStrength, onPasswordChange, setPassword]);

    useEffect(() => {
        setValue('password', password, { shouldValidate: isSubmitted })
    }, [setValue, password, isSubmitted]);

    const fieldClassNames = useCallback((field:string, cn:string[] = []) => classNames(concat(cn, isSubmitted
        ? errors[field] !== undefined
            ? 'is-invalid'
            : 'is-valid'
        : null)), [isSubmitted, errors])

    return <FormGroup>
        <Label htmlFor={`password-${id}`}>{t('Password')}</Label>
        <div className='input-group'>
            <input id={`password-${id}`} className={fieldClassNames('password', ['form-control'])} type="password" placeholder={t('Password').toString()} autoComplete="new-password" onChange={handlePasswordChange} />
            <input id={`confirmPassword-${id}`} className={fieldClassNames('confirmPassword', ['form-control'])} type="password" placeholder={t('Conferma password').toString()} autoComplete="new-password" {...registerInput('confirmPassword', {
                required: t('Campo obbligatorio') as string,
                validate: {
                    equalToPassword: (v) => watch('password') === v || t('I campi non corrispondono').toString()
                }
            })} />
        </div>

        <div>
            <FancyProgress min={0} max={10} value={passwordStrength?.value?.score ?? 0}>
                {passwordStrength?.value?.key ?? null}
            </ FancyProgress>
        </div>
        {
            errors.password !== undefined ? <div className="invalid-feedback d-block">{errors.password.message?.toString()}</div>
                : errors.confirmPassword !== undefined ? <div className="invalid-feedback d-block">{errors.confirmPassword.message?.toString()}</div>
                    : null
        }
    </FormGroup>
}


export default PasswordFormGroup;